import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const examples = [
  {
    id: 'software-engineer',
    title: 'Mid‑Level Software Engineer',
    subtitle: 'Tech • 4–6 years experience',
    summary:
      'Clean, modern layout that highlights impact, key projects, and modern tech stack. Great for product companies and fast‑growing startups.',
    highlights: [
      'Results‑focused bullet points (metrics, shipped features, performance improvements)',
      'Projects section for open‑source or side projects',
      'Skills grouped by languages, frameworks, and tools',
    ],
    templateId: 'tech-1',
  },
  {
    id: 'product-manager',
    title: 'Senior Product Manager',
    subtitle: 'Product • 7+ years experience',
    summary:
      'Executive‑style resume focused on business outcomes, cross‑functional leadership, and roadmap ownership.',
    highlights: [
      'Story‑driven summary that positions you as a strategic partner',
      'Experience bullets tied to revenue, adoption, and KPIs',
      'Stakeholder management and leadership skills called out clearly',
    ],
    templateId: 'executive-1',
  },
  {
    id: 'graduate',
    title: 'Recent Graduate / Career Switcher',
    subtitle: 'Entry‑Level • 0–2 years experience',
    summary:
      'Simple, ATS‑friendly layout that emphasizes projects, education, and transferable skills instead of years of experience.',
    highlights: [
      'Projects and coursework sections placed above experience',
      'Skills and tools aligned with the target role',
      'Short, clear bullets focused on outcomes and learnings',
    ],
    templateId: 'minimalist-1',
  },
];

const Examples = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleUseExample = (example) => {
    if (example.templateId) {
      navigate(`/builder/${example.templateId}`);
    } else {
      navigate('/resume-builder');
    }
  };

  const handleAnalyzeExample = () => {
    navigate('/resume-analyzer');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Hero */}
          <section className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('examples.heroTitle')}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('examples.heroSubtitle')}
            </p>
          </section>

          {/* Example Cards */}
          <section className="grid gap-8 md:grid-cols-2 xl:grid-cols-3 mb-12">
            {examples.map((example) => (
              <article
                key={example.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col"
              >
                <h2 className="text-xl font-semibold text-gray-900 mb-1">{example.title}</h2>
                <p className="text-sm text-primary-600 font-medium mb-3">{example.subtitle}</p>
                <p className="text-sm text-gray-700 mb-4 flex-1">{example.summary}</p>

                <div className="mb-4">
                  <h3 className="text-xs font-semibold text-gray-500 tracking-wide mb-2">
                  {t('examples.cardWhatThisDoesWell')}
                  </h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                    {example.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => handleUseExample(example)}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
                  >
                    {t('examples.useLayout')}
                  </button>
                  <button
                    onClick={handleAnalyzeExample}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    {t('examples.seeAIScore')}
                  </button>
                </div>
              </article>
            ))}
          </section>

          {/* CTA */}
          <section className="bg-primary-50 border border-primary-100 rounded-2xl p-6 md:p-8 text-center">
            <h2 className="text-2xl font-bold text-primary-900 mb-2">
              {t('examples.bottomTitle')}
            </h2>
            <p className="text-sm md:text-base text-primary-800 mb-4 max-w-2xl mx-auto">
              {t('examples.bottomText')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/resume-builder')}
                className="inline-flex justify-center items-center px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                {t('examples.openBuilder')}
              </button>
              <button
                onClick={() => navigate('/ai-recommendations')}
                className="inline-flex justify-center items-center px-5 py-2.5 rounded-lg border border-primary-200 text-sm font-medium text-primary-800 hover:bg-primary-100 transition-colors"
              >
                {t('examples.getAIRecommendations')}
              </button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Examples;

